import { ArrowDown, ArrowUp, Flame, Minus, type LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/cn';
import { TICKET_PRIORITY_LABELS, type TicketPriority } from '../tickets.schema';

// Soft tinted badge — the resting state of a priority on cards/rows.
const PRIORITY_CLASSES: Record<TicketPriority, string> = {
    low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    normal: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200',
    urgent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
};

// Solid left-stripe accent for cards/rows, colour-coded by priority. Rendered
// as an absolutely-positioned span so it never fights the element's own border.
export const PRIORITY_STRIPE: Record<TicketPriority, string> = {
    low: 'bg-gray-300 dark:bg-gray-600',
    normal: 'bg-sky-400 dark:bg-sky-500',
    high: 'bg-orange-400 dark:bg-orange-500',
    urgent: 'bg-red-500',
};

// Saturated fill for the *selected* chip in the priority button group. The
// colour does the heavy lifting for someone who can't parse the label quickly.
export const PRIORITY_ACTIVE: Record<TicketPriority, string> = {
    low: 'border-gray-500 bg-gray-500 text-white shadow-sm',
    normal: 'border-sky-500 bg-sky-500 text-white shadow-sm',
    high: 'border-orange-500 bg-orange-500 text-white shadow-sm',
    urgent: 'border-red-600 bg-red-600 text-white shadow-sm',
};

// Tints the icon on the *unselected* chip so even resting chips carry a colour cue.
export const PRIORITY_ICON_TINT: Record<TicketPriority, string> = {
    low: 'text-gray-400',
    normal: 'text-sky-500',
    high: 'text-orange-500',
    urgent: 'text-red-500',
};

// An intuitive shape per level: down = low, flat = normal, up = high, flame = urgent.
export const PRIORITY_ICONS: Record<TicketPriority, LucideIcon> = {
    low: ArrowDown,
    normal: Minus,
    high: ArrowUp,
    urgent: Flame,
};

const SIZES = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
} as const;

const ICON_SIZES = {
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
} as const;

export function TicketPriorityBadge({
    priority,
    size = 'sm',
    className,
}: {
    priority: TicketPriority;
    size?: keyof typeof SIZES;
    className?: string;
}) {
    const Icon = PRIORITY_ICONS[priority];
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full font-semibold ring-1 ring-inset ring-black/5 dark:ring-white/10',
                SIZES[size],
                PRIORITY_CLASSES[priority],
                className,
            )}
        >
            <Icon aria-hidden="true" className={ICON_SIZES[size]} />
            {TICKET_PRIORITY_LABELS[priority]}
        </span>
    );
}
